/**
 * Stage 01 — Atoms section for the Atomic Design showcase.
 * Icon, ILMU Mark, Button, Input, Label, Badge, Avatar, Kbd, Divider.
 */

import { Avatar } from '../../components/primitives/Avatar'
import { Badge } from '../../components/primitives/Badge'
import { Button } from '../../components/primitives/Button'
import { Divider } from '../../components/primitives/Divider'
import { Icon } from '../../components/primitives/Icon'
import { IlmuMark } from '../../components/primitives/IlmuMark'
import { Kbd } from '../../components/primitives/Kbd'
import { Grid3, GroupHeader, PrimitiveCard, SectionHeader } from './shared'

export function AtomsSection() {
  return (
    <section id="atoms" style={{ marginBottom: 80, scrollMarginTop: 24 }}>
      <SectionHeader
        num="01"
        name="Atoms"
        tag="Indivisible"
        stage="atoms"
        desc="The foundational primitives — design tokens, single HTML elements, and small wrappers that can't be broken down further without losing meaning."
      />

      <GroupHeader label="Primitives" />
      <Grid3>
        {/* Icon */}
        <PrimitiveCard name="Icon" tag="Atom" desc="1.6 px stroke, currentColor — 28 lucide-style glyphs.">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(8, 1fr)',
              gap: 6,
              width: '100%',
            }}
          >
            {[
              'home', 'add', 'calendar_today', 'schedule', 'group', 'checklist', 'notifications',
              'search', 'warning', 'monitor_heart', 'send', 'shield', 'work', 'lock',
              'filter_alt', 'settings',
            ].map((name) => (
              <span
                key={name}
                style={{
                  aspectRatio: '1',
                  display: 'grid',
                  placeItems: 'center',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  color: 'var(--text-2)',
                  background: 'var(--surface)',
                }}
              >
                <Icon name={name as Parameters<typeof Icon>[0]['name']} size={16} />
              </span>
            ))}
          </div>
        </PrimitiveCard>

        {/* ILMU Mark */}
        <PrimitiveCard name="ILMU Mark" tag="Atom · Brand" desc="The flower glyph that represents Lumen's AI character." dark>
          <IlmuMark size={64} aria-label="ILMU mark" />
        </PrimitiveCard>

        {/* Button */}
        <PrimitiveCard
          name="Button"
          tag="Atom"
          desc="Six variants × three sizes. Navy uses Space Cadet, AI uses navy on Pear."
          col
        >
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="navy">Primary</Button>
            <Button variant="blue">Blue</Button>
            <Button variant="secondary">Ghost</Button>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="soft">Soft</Button>
            <Button variant="ai">Ask ILMU</Button>
            <Button variant="danger-outline">Danger</Button>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' }}>
            <Button variant="navy" size="sm">Small</Button>
            <Button variant="navy" size="md">Default</Button>
            <Button variant="navy" size="lg">Large</Button>
          </div>
        </PrimitiveCard>

        {/* Input */}
        <PrimitiveCard name="Input" tag="Atom" desc="Single text control. Focus ring is Steel Blue with a 3 px Baby Blue Tint glow." col>
          <input
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border-strong)',
              borderRadius: 9,
              padding: '9px 12px',
              fontSize: 13.5,
              width: '100%',
              outline: 'none',
            }}
            placeholder="e.g. Hari Raya leave"
          />
          <input
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border-strong)',
              borderRadius: 9,
              padding: '9px 12px',
              fontSize: 13.5,
              width: '100%',
              outline: 'none',
            }}
            defaultValue="Family vacation"
          />
        </PrimitiveCard>

        {/* Label */}
        <PrimitiveCard name="Label" tag="Atom" desc="12.5 px / 500. Always paired with a control.">
          <span style={{ fontSize: 12.5, color: 'var(--text-2)', fontWeight: 500 }}>
            Reason for leave
          </span>
        </PrimitiveCard>

        {/* Badge */}
        <PrimitiveCard
          name="Badge"
          tag="Atom"
          desc="Status, count, and category. Comes in 9 semantic variants including blue, pear, navy."
          col
        >
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Badge variant="blue">Annual</Badge>
            <Badge variant="pear">Emergency</Badge>
            <Badge variant="navy">ILMU</Badge>
            <Badge variant="default">Cancelled</Badge>
          </div>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Badge variant="success" dot>Approved</Badge>
            <Badge variant="warning" dot>Pending</Badge>
            <Badge variant="danger" dot>Rejected</Badge>
          </div>
        </PrimitiveCard>

        {/* Avatar */}
        <PrimitiveCard name="Avatar" tag="Atom" desc="Initials, persona color. Four sizes from xs to lg.">
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 14,
              alignItems: 'flex-end',
            }}
          >
            <Avatar name="Aisyah Rahman" size={20} />
            <Avatar name="Hafiz Zainudin" size={24} />
            <Avatar name="Priya Sundaram" size={30} />
            <Avatar name="Reza Karim" size={40} />
          </div>
        </PrimitiveCard>

        {/* Kbd */}
        <PrimitiveCard name="Kbd" tag="Atom" desc="A single keypress in monospace, with a hairline border.">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4 }}>
            <Kbd>⌘</Kbd>
            <Kbd>K</Kbd>
            <span style={{ color: 'var(--text-3)', margin: '0 4px' }}>·</span>
            <Kbd>esc</Kbd>
            <span style={{ color: 'var(--text-3)', margin: '0 4px' }}>·</span>
            <Kbd>↵</Kbd>
          </div>
        </PrimitiveCard>

        {/* Divider */}
        <PrimitiveCard name="Divider" tag="Atom" desc="1 px --border. Soft separator between content blocks." noPad>
          <div style={{ padding: '30px 24px', width: '100%' }}>
            <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginBottom: 14 }}>
              First section
            </div>
            <Divider />
            <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 14 }}>
              Second section
            </div>
          </div>
        </PrimitiveCard>
      </Grid3>
    </section>
  )
}
